import React, { useState, useEffect, useRef } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { doc, getDoc, setDoc, updateDoc, collection, addDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { Rnd } from "react-rnd";
import AgoraRTC from "agora-rtc-sdk-ng";

// --- ثابت‌های بازی و Agora ---
const INITIAL_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const AGORA_APP_ID = "9e4b1aa26be44917ba88059e009d7b66";
const AGORA_CHANNEL = "room123";
const AGORA_TOKEN =
  "007eJxTYOjukJwm0rumfaFGdqDi1tjvLhvZK0Muzmat7Lq7u/Lt1vcKDJapJkmGiYlGZkmpJiaWhuZJiRYWBqaWqQYGlinmSWZm3oLTMxoCGRlMnWRYGBkgEMRnZyjKz881NDJmYAAAjqMe8A==";

const UID = Math.floor(Math.random() * 1000000);

const ChessGamePage = () => {
  const { roomCode } = useParams();
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  // --- حالت‌ها (State) ---
  const [game, setGame] = useState(() => new Chess(INITIAL_FEN));
  const [fen, setFen] = useState(INITIAL_FEN);
  const [playerRole, setPlayerRole] = useState('spectator');
  const [players, setPlayers] = useState({
    white: null,
    black: null,
    whiteName: '',
    blackName: ''
  });
  const [gameStarted, setGameStarted] = useState(false);
  const [whiteTime, setWhiteTime] = useState(300);
  const [blackTime, setBlackTime] = useState(300);
  const [moveHistory, setMoveHistory] = useState([]);
  const [selectedTime, setSelectedTime] = useState(5);
  const [loading, setLoading] = useState(true);
  const [micOn, setMicOn] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  
  // --- رفرنس‌ها (Ref) ---
  const timerRef = useRef(null);
  const rtcClientRef = useRef(null);
  const localMicTrackRef = useRef(null);
  const messagesEndRef = useRef(null);

  // --- رفرنس‌های Firebase ---
  const roomRef = doc(db, 'rooms', roomCode);
  const gameRef = doc(db, 'games', roomCode);
  const movesRef = collection(db, 'games', roomCode, 'moves');
  const messagesRef = collection(db, 'rooms', roomCode, 'messages');

  const timeOptions = [
    { value: 1, label: '1 دقیقه' },
    { value: 3, label: '3 دقیقه' },
    { value: 5, label: '5 دقیقه' },
    { value: 10, label: '10 دقیقه' },
  ];

  // --- useEffect برای راه‌اندازی بازی، تعیین نقش و اتصال به Firebase ---
  useEffect(() => {
    const initializeGame = async () => {
      try {
        setLoading(true);
        
        // ایجاد یا بارگذاری اتاق
        const roomSnap = await getDoc(roomRef);
        if (!roomSnap.exists()) {
          await setDoc(roomRef, {
            createdAt: new Date().toISOString(),
            status: 'waiting',
            players: [],
            whitePlayer: null,
            blackPlayer: null,
            whitePlayerName: '',
            blackPlayerName: ''
          });
        }

        // ایجاد یا بارگذاری بازی
        const gameSnap = await getDoc(gameRef);
        if (!gameSnap.exists()) {
          await setDoc(gameRef, {
            fen: INITIAL_FEN,
            gameStarted: false,
            whiteTime: selectedTime * 60,
            blackTime: selectedTime * 60,
            timeControl: selectedTime,
            createdAt: new Date().toISOString()
          });
        }

        await determinePlayerRole();

      } catch (error) {
        console.error('خطا در راه‌اندازی بازی:', error);
      } finally {
        setLoading(false);
      }
    };

    const determinePlayerRole = async () => {
      const roomSnap = await getDoc(roomRef);
      if (roomSnap.exists()) {
        const roomData = roomSnap.data();
        
        if (!roomData.whitePlayer) {
          await setDoc(roomRef, {
            whitePlayer: auth.currentUser.uid,
            whitePlayerName: auth.currentUser.displayName || 'بازیکن سفید',
            players: [...(roomData.players || []), auth.currentUser.uid]
          }, { merge: true });
          setPlayerRole('white');
        } else if (!roomData.blackPlayer) {
          await setDoc(roomRef, {
            blackPlayer: auth.currentUser.uid,
            blackPlayerName: auth.currentUser.displayName || 'بازیکن سیاه',
            players: [...(roomData.players || []), auth.currentUser.uid]
          }, { merge: true });
          setPlayerRole('black');
        } else {
          setPlayerRole('spectator');
        }
        
        setPlayers({
          white: roomData.whitePlayer,
          black: roomData.blackPlayer,
          whiteName: roomData.whitePlayerName,
          blackName: roomData.blackPlayerName
        });
      }
    };
    
    // اتصال به Agora
    const joinAgora = async () => {
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      rtcClientRef.current = client;

      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        if (mediaType === "audio") user.audioTrack?.play();
      });

      try {
        await client.join(AGORA_APP_ID, AGORA_CHANNEL, AGORA_TOKEN, UID);
      } catch (error) {
        console.error("خطا در اتصال به Agora:", error);
      }
    };

    initializeGame();
    joinAgora();

    // مشاهده تغییرات بازی در لحظه
    const unsubscribeGame = onSnapshot(gameRef, (doc) => {
      if (doc.exists()) {
        const gameData = doc.data();
        try {
          const chess = new Chess(gameData.fen || INITIAL_FEN);
          setGame(chess);
          setFen(chess.fen());
          setGameStarted(gameData.gameStarted || false);
          setWhiteTime(gameData.whiteTime || selectedTime * 60);
          setBlackTime(gameData.blackTime || selectedTime * 60);
        } catch (error) {
          console.error('خطا در پردازش وضعیت بازی:', error);
          setGame(new Chess(INITIAL_FEN));
          setFen(INITIAL_FEN);
        }
      }
    });

    // مشاهده تاریخچه حرکات
    const unsubscribeMoves = onSnapshot(movesRef, (snapshot) => {
      const moves = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => a.timestamp - b.timestamp);
      setMoveHistory(moves);
    });

    // مشاهده پیام‌های چت
    const q = query(messagesRef, orderBy("createdAt", "asc"));
    const unsubMessages = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubscribeGame();
      unsubscribeMoves();
      unsubMessages();
      clearInterval(timerRef.current);
      rtcClientRef.current?.leave();
    };
  }, [roomCode]);

  // اسکرول خودکار به انتهای پیام‌ها
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // --- useEffect برای مدیریت زمان‌سنج بازی ---
  useEffect(() => {
    if (!gameStarted || game.isGameOver()) {
      clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(async () => {
      try {
        if (game.turn() === 'w') {
          setWhiteTime(prev => {
            const newTime = prev - 1;
            if (newTime <= 0) {
              handleGameEnd('timeout', 'white');
              return 0;
            }
            return newTime;
          });
        } else {
          setBlackTime(prev => {
            const newTime = prev - 1;
            if (newTime <= 0) {
              handleGameEnd('timeout', 'black');
              return 0;
            }
            return newTime;
          });
        }

        // به‌روزرسانی زمان در Firebase هر 1 ثانیه
        await updateDoc(gameRef, {
          whiteTime,
          blackTime
        });
      } catch (error) {
        console.error('خطا در به‌روزرسانی زمان:', error);
      }
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [gameStarted, game.turn(), whiteTime, blackTime]);

  // --- توابع مدیریت بازی ---
  const startGame = async () => {
    try {
      const initialTime = selectedTime * 60;
      await setDoc(gameRef, {
        fen: INITIAL_FEN,
        gameStarted: true,
        whiteTime: initialTime,
        blackTime: initialTime,
        timeControl: selectedTime,
        startTime: new Date().toISOString(),
        gameOver: false,
        result: null
      }, { merge: true });
      
      setWhiteTime(initialTime);
      setBlackTime(initialTime);
      setGameStarted(true);
      setGame(new Chess(INITIAL_FEN));
      setFen(INITIAL_FEN);
    } catch (error) {
      console.error('خطا در شروع بازی:', error);
    }
  };

  const handleGameEnd = async (reason, loser) => {
    clearInterval(timerRef.current);
    let result = '';
    
    if (reason === 'timeout') {
      result = loser === 'white' ? 'black' : 'white';
      await updateDoc(gameRef, {
        gameOver: true,
        result: `${result}-time`,
        endTime: new Date().toISOString()
      });
    } else if (reason === 'checkmate') {
      result = game.turn() === 'w' ? 'black' : 'white';
      await updateDoc(gameRef, {
        gameOver: true,
        result: `${result}-checkmate`,
        endTime: new Date().toISOString()
      });
    }
  };

  const onDrop = async (sourceSquare, targetSquare) => {
    if (!gameStarted || playerRole === 'spectator') return false;
    if (
      (playerRole === 'white' && game.turn() !== 'w') ||
      (playerRole === 'black' && game.turn() !== 'b')
    ) return false;

    try {
      const move = game.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q',
      });

      if (!move) return false;

      // ذخیره حرکت در تاریخچه
      await addDoc(movesRef, {
        from: sourceSquare,
        to: targetSquare,
        piece: move.piece,
        color: move.color,
        san: move.san,
        fen: game.fen(),
        player: auth.currentUser.uid,
        playerName: auth.currentUser.displayName || (playerRole === 'white' ? 'سفید' : 'سیاه'),
        timestamp: new Date().getTime(),
        moveNumber: Math.floor(moveHistory.length / 2) + 1,
        isWhiteMove: move.color === 'w'
      });

      // به روز رسانی وضعیت بازی
      const newGame = new Chess(game.fen());
      await updateDoc(gameRef, {
        fen: newGame.fen(),
        turn: newGame.turn(),
        lastMove: {
          from: sourceSquare,
          to: targetSquare,
          player: auth.currentUser.uid,
          timestamp: new Date().toISOString()
        }
      });

      if (newGame.isCheckmate()) {
        handleGameEnd('checkmate');
      }

      return true;
    } catch (error) {
      console.error('خطا در انجام حرکت:', error);
      return false;
    }
  };
  
  // --- تابع مدیریت میکروفن ---
  const toggleMic = async () => {
    try {
      if (!micOn) {
        const mic = await AgoraRTC.createMicrophoneAudioTrack({
          encoderConfig: { sampleRate: 48000, stereo: true, bitrate: 128 },
          AEC: true,
          AGC: true,
          ANS: true,
        });
        localMicTrackRef.current = mic;
        await rtcClientRef.current.publish([mic]);
      } else {
        await rtcClientRef.current.unpublish([localMicTrackRef.current]);
        localMicTrackRef.current.stop();
        localMicTrackRef.current.close();
        localMicTrackRef.current = null;
      }
      setMicOn(!micOn);
    } catch (e) {
      console.error("Mic error:", e);
    }
  };
  
  // --- تابع مدیریت پیام‌های چت ---
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;
    await addDoc(messagesRef, {
      userId,
      text: messageText.trim(),
      createdAt: new Date(),
    });
    setMessageText("");
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  if (loading) {
    return <div style={{ padding: 20, textAlign: 'center' }}>در حال بارگذاری بازی...</div>;
  }

  return (
    <div style={{
      display: 'flex',
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      gap: '20px',
      position: 'relative'
    }}>
      {/* تخته شطرنج */}
      <div style={{ flex: 2 }}>
        <Chessboard
          position={fen}
          onPieceDrop={onDrop}
          boardWidth={600}
          customBoardStyle={{
            borderRadius: '4px',
            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.5)'
          }}
          customDarkSquareStyle={{ backgroundColor: '#779556' }}
          customLightSquareStyle={{ backgroundColor: '#ebecd0' }}
        />
      </div>

      {/* پنل وضعیت بازی در سمت راست */}
      <div style={{
        flex: 1,
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div>
          <h3 style={{ marginBottom: '15px', borderBottom: '1px solid #ddd', paddingBottom: '10px' }}>
            وضعیت بازی
          </h3>
          <p style={{ marginBottom: '10px' }}>
            <strong>نقش شما:</strong>
            <span style={{
              color: playerRole === 'white' ? '#333' : playerRole === 'black' ? '#333' : '#666',
              fontWeight: 'bold'
            }}>
              {playerRole === 'white' ? ' سفید' : playerRole === 'black' ? ' سیاه' : ' تماشاگر'}
            </span>
          </p>

          <div style={{
            backgroundColor: playerRole === 'white' ? '#f0f8ff' : '#fff',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '15px'
          }}>
            <p style={{ fontWeight: 'bold' }}>بازیکن سفید:</p>
            <p>{players.whiteName || 'در انتظار...'}</p>
            <p>
              زمان:
              <span style={{
                color: game.turn() === 'w' ? '#d9534f' : '#333',
                fontWeight: 'bold',
                marginRight: '5px'
              }}>
                {formatTime(whiteTime)}
              </span>
            </p>
          </div>

          <div style={{
            backgroundColor: playerRole === 'black' ? '#f0f8ff' : '#fff',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '15px'
          }}>
            <p style={{ fontWeight: 'bold' }}>بازیکن سیاه:</p>
            <p>{players.blackName || 'در انتظار...'}</p>
            <p>
              زمان:
              <span style={{
                color: game.turn() === 'b' ? '#d9534f' : '#333',
                fontWeight: 'bold',
                marginRight: '5px'
              }}>
                {formatTime(blackTime)}
              </span>
            </p>
          </div>

          <p style={{ marginBottom: '10px' }}>
            <strong>نوبت:</strong>
            <span style={{
              color: game.turn() === 'w' ? '#333' : '#333',
              fontWeight: 'bold'
            }}>
              {game.turn() === 'w' ? ' سفید' : ' سیاه'}
            </span>
          </p>

          {game.isCheck() && (
            <p style={{ color: '#d9534f', fontWeight: 'bold' }}>وضعیت: کیش!</p>
          )}
          {game.isCheckmate() && (
            <p style={{ color: '#d9534f', fontWeight: 'bold' }}>وضعیت: کیش مات!</p>
          )}
        </div>
        
        <div style={{ marginTop: '20px' }}>
          <button
            onClick={toggleMic}
            style={{
              padding: '8px 16px',
              backgroundColor: micOn ? '#f44336' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            {micOn ? 'قطع میکروفن' : 'پخش صدا'}
          </button>
        </div>

        {!gameStarted && (playerRole === 'white' || playerRole === 'black') && (
          <div style={{ marginTop: '20px' }}>
            <select
              value={selectedTime}
              onChange={(e) => setSelectedTime(parseInt(e.target.value))}
              style={{
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                marginRight: '10px'
              }}
            >
              {timeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              onClick={startGame}
              style={{
                padding: '8px 16px',
                backgroundColor: '#5cb85c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              شروع بازی
            </button>
          </div>
        )}

        <div style={{ marginTop: '20px' }}>
          <h4 style={{ borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>تاریخچه حرکات</h4>
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: '#fff',
            padding: '10px',
            borderRadius: '4px'
          }}>
            {moveHistory.length === 0 ? (
              <p style={{ color: '#777' }}>هنوز حرکتی انجام نشده است</p>
            ) : (
              <div>
                {Array.from(new Set(moveHistory.map(move => move.moveNumber))).map((moveNum) => {
                  const whiteMove = moveHistory.find(m => m.moveNumber === moveNum && m.isWhiteMove);
                  const blackMove = moveHistory.find(m => m.moveNumber === moveNum && !m.isWhiteMove);

                  return (
                    <div key={moveNum} style={{
                      padding: '6px 0',
                      borderBottom: '1px solid #eee'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{moveNum}. {whiteMove?.san || ''}</span>
                        <span style={{ color: '#666', fontSize: '0.9em' }}>
                          {whiteMove?.playerName || ''}
                        </span>
                      </div>
                      {blackMove && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                          <span>{moveNum}... {blackMove.san}</span>
                          <span style={{ color: '#666', fontSize: '0.9em' }}>
                            {blackMove.playerName}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <Rnd
        default={{
          x: window.innerWidth - 320,
          y: 100,
          width: 300,
          height: 400,
        }}
        bounds="window"
        enableResizing
        minWidth={250}
        minHeight={300}
      >
        <div
          style={{
            background: "#fff",
            border: "1px solid #ccc",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            borderRadius: 8,
            overflow: "hidden",
            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: 8,
              backgroundColor: "#f5f5f5",
            }}
          >
            {messages.map((m) => (
              <div
                key={m.id}
                style={{
                  marginBottom: 8,
                  padding: 8,
                  backgroundColor: m.userId === userId ? "#d1ffd6" : "#fff",
                  borderRadius: 6,
                  border: "1px solid #ccc",
                }}
              >
                <div style={{ fontSize: 12, color: "#555" }}>
                  {m.userId.slice(0, 6)}
                </div>
                <div>{m.text}</div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <form
            onSubmit={handleSendMessage}
            style={{
              display: "flex",
              gap: 8,
              padding: 8,
              borderTop: "1px solid #ccc",
            }}
          >
            <input
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="پیام خود را بنویسید..."
              style={{
                flex: 1,
                padding: 8,
                border: "1px solid #ddd",
                borderRadius: 4,
              }}
            />
            <button
              type="submit"
              style={{
                padding: "8px 16px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              ارسال
            </button>
          </form>
        </div>
      </Rnd>
    </div>
  );
};

export default ChessGamePage;